import React, { useMemo, useCallback } from "react";
import { Table, Button, Input, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useCountryList } from "@/hooks/useCountryList";
import styles from "./styles.module.less";

// ==================== 类型定义 ====================
export interface BankInfo {
  id?: string;
  accountType?: "corporate" | "personal"; // 银行账户类型：公户/私户
  country?: string; // 银行国家
  openBankName?: string; // 银行名称 / 开户行名称
  openBankNumber?: string; // 银行行号 / 开户行行号
  account?: string; // 银行账号
  swiftCode?: string; // SWIFT代码
  accountHolder?: string; // 收款人 / 开户人/单位
  openBankInSap?: number; // 是否在SAP中：1=存在, 0=不存在
}

interface BankInfoTableProps {
  value?: BankInfo[];
  onChange?: (value: BankInfo[]) => void;
  onAddBank?: () => void;
  readOnly?: boolean;
  showSapWarning?: boolean;
  errorBankNumbers?: string[]; // 新增：用于标记 SAP 不存在的开户行行号
}

// ==================== 常量配置 ====================

const getAccountTypeOptions = (t: (key: string) => string) => [
  { label: t("公户"), value: "corporate" },
  { label: t("私户"), value: "personal" },
];

// ==================== 主组件 ====================
const BankInfoTable: React.FC<BankInfoTableProps> = ({
  value = [],
  onChange,
  readOnly = false,
  showSapWarning = true,
  errorBankNumbers = [],
}) => {
  const { t } = useTranslation("supplierProfile");
  const { countryList, loading: countryLoading } = useCountryList();

  // ==================== 辅助函数 ====================
  /**
   * 将 countryCode 转换为 areaCode（用于表单显示）
   * 如果 country 是数字（countryCode），查找对应的 areaCode
   */
  const normalizeCountryValue = useCallback(
    (countryValue: string | number | undefined) => {
      if (!countryValue) return countryValue;

      const countryStr = String(countryValue);
      const countryNum = Number(countryStr);

      // 如果是数字，检查是否需要转换
      if (!isNaN(countryNum) && countryNum > 0) {
        // 查找是否存在匹配的 areaCode
        const found = countryList.find(
          (item) =>
            String(item.areaCode) === countryStr ||
            Number(item.areaCode) === countryNum,
        );
        if (found) {
          // 已经是 areaCode，直接返回
          return found.areaCode;
        }

        // 查找是否存在匹配的 code（countryCode）
        const foundByCode = countryList.find(
          (item) => String(item.code) === countryStr,
        );
        if (foundByCode) {
          // 是 countryCode，返回对应的 areaCode
          return foundByCode.areaCode;
        }
      }

      return countryValue;
    },
    [countryList],
  );

  // ==================== 事件处理函数 ====================
  const handleAddBank = useCallback(() => {
    const newBank: BankInfo = {
      // 新增的银行不设置 id，仅在编辑时后端返回 id
      accountType: undefined,
      country: "",
      openBankName: "",
      openBankNumber: "",
      account: "",
      swiftCode: "",
      accountHolder: "",
    };
    const newData = [...value, newBank];
    onChange?.(newData);
  }, [value, onChange]);

  const handleFieldChange = useCallback(
    (index: number, field: keyof BankInfo, fieldValue: any) => {
      const newData = [...value];
      newData[index] = {
        ...newData[index],
        [field]: fieldValue,
      };
      onChange?.(newData);
    },
    [value, onChange],
  );

  const handleDeleteBank = useCallback(
    (index: number) => {
      const newData = value.filter((_, i) => i !== index);
      onChange?.(newData);
    },
    [value, onChange],
  );

  // ==================== 渲染函数 ====================
  /**
   * 检查是否存在 SAP 不存在的开户行
   */
  const hasSapMissingBank = useMemo(() => {
    return value.some((bank) => bank.openBankInSap === 0);
  }, [value]);

  /**
   * 获取缺失SAP的行号集合
   */
  const sapMissingRowIndices = useMemo(() => {
    const indices = new Set<number>();
    value.forEach((bank, index) => {
      // 优先检查 errorBankNumbers（来自 SAP 验证失败的行号）
      if (errorBankNumbers.includes(bank.openBankNumber || "")) {
        indices.add(index);
      }
      // 其次检查 openBankInSap 标记
      else if (bank.openBankInSap === 0) {
        indices.add(index);
      }
    });
    return indices;
  }, [value, errorBankNumbers]);

  // ==================== 配置项 ====================
  const countryOptions = useMemo(() => {
    if (!Array.isArray(countryList)) {
      return [];
    }
    return countryList.map((item) => ({
      label: item.shortName,
      value: item.areaCode,
    }));
  }, [countryList]);

  const getCountryName = useCallback(
    (countryValue: string | number) => {
      if (!Array.isArray(countryList)) {
        return countryValue;
      }

      // 如果已经是 areaCode（数字），直接查找
      if (typeof countryValue === "number") {
        return (
          countryList.find((item) => item.areaCode === countryValue)
            ?.shortName || countryValue
        );
      }

      // 如果是字符串，尝试多种匹配方式
      const countryStr = String(countryValue);
      const countryNum = Number(countryStr);

      // 1. 尝试匹配 areaCode（字符串形式或数字）
      let found = countryList.find(
        (item) =>
          String(item.areaCode) === countryStr ||
          (item.areaCode && Number(item.areaCode) === countryNum),
      );
      if (found) return found.shortName;

      // 2. 尝试匹配 code（countryCode）
      found = countryList.find((item) => String(item.code) === countryStr);
      if (found) return found.shortName;

      // 3. 尝试匹配 shortName
      found = countryList.find((item) => item.shortName === countryStr);
      if (found) return found.shortName;

      // 4. 尝试匹配 name（国家全名）
      found = countryList.find((item) => item.name === countryStr);
      if (found) return found.shortName;

      // 如果都没匹配到，返回原值
      return countryValue;
    },
    [countryList],
  );

  // ==================== 表格列配置 ====================
  const columns: ColumnsType<BankInfo> = useMemo(
    () => [
      {
        title: t("序号"),
        key: "index",
        width: 60,
        render: (_, __, index) => index + 1,
      },
      {
        title: t("银行账户类型"),
        dataIndex: "accountType",
        key: "accountType",
        width: 120,
        render: (accountType, _, index) =>
          readOnly ? (
            <span>
              {accountType === "corporate"
                ? t("公户")
                : accountType === "personal"
                  ? t("私户")
                  : accountType}
            </span>
          ) : (
            <Select
              value={accountType}
              options={getAccountTypeOptions(t)}
              onChange={(value) =>
                handleFieldChange(index, "accountType", value)
              }
              placeholder={t("请选择")}
              style={{ width: "100%" }}
            />
          ),
      },
      {
        title: t("银行国家"),
        dataIndex: "country",
        key: "country",
        width: 120,
        render: (country, _, index) => {
          const normalizedCountry = normalizeCountryValue(country);
          return readOnly ? (
            <span>{getCountryName(normalizedCountry)}</span>
          ) : (
            <Select
              value={normalizedCountry || undefined}
              options={countryOptions}
              onChange={(value) => handleFieldChange(index, "country", value)}
              placeholder={t("请选择")}
              style={{ width: "100%" }}
              loading={countryLoading}
            />
          );
        },
      },
      {
        title: t("开户行名称"),
        dataIndex: "openBankName",
        key: "openBankName",
        width: 150,
        render: (openBankName, record, index) => {
          const isSapMissing = sapMissingRowIndices.has(index);
          return readOnly ? (
            <span style={{ color: isSapMissing ? "#ff4d4f" : "inherit" }}>
              {openBankName}
            </span>
          ) : (
            <Input
              value={openBankName || ""}
              onChange={(e) =>
                handleFieldChange(index, "openBankName", e.target.value)
              }
              placeholder={t("请输入")}
              // style={{
              //   borderColor: isSapMissing ? "#ff4d4f" : undefined,
              //   color: isSapMissing ? "#ff4d4f" : "inherit",
              // }}
            />
          );
        },
      },
      {
        title: t("开户行行号"),
        dataIndex: "openBankNumber",
        key: "openBankNumber",
        width: 150,
        render: (openBankNumber, record, index) => {
          const isSapMissing = sapMissingRowIndices.has(index);
          return readOnly ? (
            <span style={{ color: isSapMissing ? "#ff4d4f" : "inherit" }}>
              {openBankNumber}
            </span>
          ) : (
            <Input
              value={openBankNumber || ""}
              onChange={(e) =>
                handleFieldChange(index, "openBankNumber", e.target.value)
              }
              placeholder={t("请输入")}
              // style={{
              //   borderColor: isSapMissing ? "#ff4d4f" : undefined,
              //   color: isSapMissing ? "#ff4d4f" : "inherit",
              // }}
            />
          );
        },
      },
      {
        title: t("银行账号"),
        dataIndex: "account",
        key: "account",
        width: 150,
        render: (account, _, index) =>
          readOnly ? (
            <span>{account}</span>
          ) : (
            <Input
              value={account || ""}
              onChange={(e) =>
                handleFieldChange(index, "account", e.target.value)
              }
              placeholder={t("请输入")}
            />
          ),
      },
      {
        title: t("SWIFT代码"),
        dataIndex: "swiftCode",
        key: "swiftCode",
        width: 150,
        render: (swiftCode, _, index) =>
          readOnly ? (
            <span>{swiftCode}</span>
          ) : (
            <Input
              value={swiftCode || ""}
              onChange={(e) =>
                handleFieldChange(index, "swiftCode", e.target.value)
              }
              placeholder={t("请输入")}
            />
          ),
      },
      {
        title: t("开户人单位"),
        dataIndex: "accountHolder",
        key: "accountHolder",
        width: 120,
        render: (accountHolder, _, index) =>
          readOnly ? (
            <span>{accountHolder}</span>
          ) : (
            <Input
              value={accountHolder || ""}
              onChange={(e) =>
                handleFieldChange(index, "accountHolder", e.target.value)
              }
              placeholder={t("请输入")}
            />
          ),
      },
      {
        title: t("操作"),
        key: "actions",
        width: 80,
        fixed: "right",
        render: (_, __, index) =>
          readOnly ? null : (
            <Button
              type="text"
              danger
              onClick={() => handleDeleteBank(index)}
              size="small"
            >
              {t("删除")}
            </Button>
          ),
      },
    ],
    [
      handleFieldChange,
      handleDeleteBank,
      readOnly,
      sapMissingRowIndices,
      countryOptions,
      countryLoading,
      getCountryName,
      normalizeCountryValue,
      t,
    ],
  );

  return (
    <div className={styles.bankInfoTableWrapper}>
      <div style={{ marginBottom: "12px" }}>
        {hasSapMissingBank && showSapWarning && (
          <span
            style={{ color: "#ff4d4f", marginLeft: "12px", fontSize: "13px" }}
          >
            {t("SAP不存在提示")}
          </span>
        )}
      </div>
      <Table
        columns={columns}
        bordered
        dataSource={value}
        rowKey={(record, index) => record.id || `new_${index}`}
        pagination={false}
        size="small"
        className={styles.table}
      />
      {!readOnly && (
        <div className={styles.tableFooter}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddBank}
          >
            {t("添加")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BankInfoTable;
